/**
 * A WorldSource abstracts "where the world files come from":
 *  - a ZIP upload (browser / desktop)            -> ZipSource
 *  - a picked folder (<input webkitdirectory>)   -> FileListSource
 *  - later: Tauri native fs, HTTP (hosted/collab) -> same interface
 *
 * Everything is structured-cloneable input (Blob / File[]), so a source can be
 * re-created inside every web worker without copying world data.
 */
import { ZipReader } from "./zip.ts";

export interface FileSource {
  paths(): string[];
  read(path: string): Promise<Uint8Array>;
}

export type SourceInit =
  | { kind: "zip"; blob: Blob }
  | { kind: "files"; files: { path: string; file: Blob }[] }
  | { kind: "http"; baseUrl: string; paths: string[] }
  /**
   * explicit URL per file (desktop: Tauri asset-protocol URLs of a local world folder; guests: the host's shared files).
   * `headers` go with every request (a shared world's room token); `cache` defaults to "no-store" (local files change).
   */
  | { kind: "urls"; files: { path: string; url: string }[]; headers?: Record<string, string>; cache?: RequestCache }
  /** MC Planner's generated demo island: no files, the world workers make its chunks (apps/web lib/demoWorld.ts) */
  | { kind: "demo" };

export async function openSource(init: SourceInit): Promise<FileSource> {
  if (init.kind === "demo") return { paths: () => [], read: async (p) => { throw new Error(`File not found: ${p}`); } };
  if (init.kind === "zip") {
    const z = await ZipReader.open(init.blob);
    return { paths: () => [...z.entries.keys()], read: (p) => z.read(p) };
  }
  if (init.kind === "files") {
    const map = new Map(init.files.map((f) => [f.path.replace(/\\/g, "/"), f.file]));
    return {
      paths: () => [...map.keys()],
      read: async (p) => {
        const f = map.get(p);
        if (!f) throw new Error(`File not found: ${p}`);
        return new Uint8Array(await f.arrayBuffer());
      },
    };
  }
  if (init.kind === "urls") {
    const map = new Map(init.files.map((f) => [f.path, f.url]));
    return {
      paths: () => [...map.keys()],
      read: async (p) => {
        const u = map.get(p);
        if (!u) throw new Error(`File not found: ${p}`);
        const r = await fetch(u, { cache: init.cache ?? "no-store", headers: init.headers });
        if (!r.ok) throw new Error(`HTTP ${r.status} for ${p}`);
        return new Uint8Array(await r.arrayBuffer());
      },
    };
  }
  const base = init.baseUrl.replace(/\/$/, "");
  return {
    paths: () => init.paths,
    read: async (p) => {
      const r = await fetch(`${base}/${p.split("/").map(encodeURIComponent).join("/")}`);
      if (!r.ok) throw new Error(`HTTP ${r.status} for ${p}`);
      return new Uint8Array(await r.arrayBuffer());
    },
  };
}

export interface DimensionInfo {
  /** e.g. "minecraft:overworld", "minecraft:the_nether", "mymod:mining" */
  id: string;
  /** path prefix containing region/, e.g. "world/dimensions/minecraft/overworld/" or "world/" */
  dir: string;
  regions: { rx: number; rz: number; path: string }[];
}

export interface WorldLayout {
  root: string; // prefix incl. trailing slash ("" if level.dat at top)
  dimensions: DimensionInfo[];
}

const REGION_RE = /^(.*?)region\/r\.(-?\d+)\.(-?\d+)\.mca$/;

/** Detect the world root(s) and every dimension that has region files. */
export function detectWorlds(paths: string[]): WorldLayout[] {
  const roots = paths
    .filter((p) => p.endsWith("level.dat") && (p === "level.dat" || p.endsWith("/level.dat")))
    .map((p) => p.slice(0, -"level.dat".length))
    .sort((a, b) => a.length - b.length);
  const layouts: WorldLayout[] = [];
  for (const root of roots) {
    // skip nested roots (e.g. a backup inside a world)
    if (layouts.some((l) => root.startsWith(l.root) && root !== l.root)) continue;
    const dims = new Map<string, DimensionInfo>();
    for (const p of paths) {
      if (!p.startsWith(root)) continue;
      const rel = p.slice(root.length);
      const m = REGION_RE.exec(rel);
      if (!m) continue;
      const dirRel = m[1];
      const id = dimensionIdFor(dirRel);
      if (!id) continue;
      let d = dims.get(id);
      if (!d) dims.set(id, (d = { id, dir: root + dirRel, regions: [] }));
      d.regions.push({ rx: +m[2], rz: +m[3], path: p });
    }
    const order = ["minecraft:overworld", "minecraft:the_nether", "minecraft:the_end"];
    const dimensions = [...dims.values()].sort((a, b) => {
      const ia = order.indexOf(a.id), ib = order.indexOf(b.id);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.id.localeCompare(b.id);
    });
    layouts.push({ root, dimensions });
  }
  return layouts;
}

function dimensionIdFor(dirRel: string): string | null {
  if (dirRel === "") return "minecraft:overworld"; // pre-26.x layout
  if (dirRel === "DIM-1/") return "minecraft:the_nether";
  if (dirRel === "DIM1/") return "minecraft:the_end";
  // 26.x layout (dimensions/<ns>/<path>/) and modded dims in older versions
  const m = /^dimensions\/([^/]+)\/(.+)\/$/.exec(dirRel);
  if (m) return `${m[1]}:${m[2]}`;
  return null;
}
